// apollo
import { createHttpLink, from, split } from '@apollo/client/core'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { createClient } from 'graphql-ws'
import { getMainDefinition } from '@apollo/client/utilities'
import { onError } from '@apollo/client/link/error'
import { setContext } from '@apollo/client/link/context'

// external librares
import { TokenRefreshLink } from 'apollo-link-token-refresh'
import { jwtDecode } from 'jwt-decode'

// enums
import EAccessTokenStates from '@/ts/enums/EAccessTokenStates'
import EApolloClients from '@/ts/enums/EApolloClients'
import EContentType from '@/ts/enums/EContentType'
import ECookies from '@/ts/enums/ECookies'
import EFetchCredentials from '@/ts/enums/EFetchCredentials'
import EFetchMethod from '@/ts/enums/EFetchMethod'
import EGrantType from '@/ts/enums/EGrantType'
import EHeaders from '@/ts/enums/EHeaders'
import EHookKeys from '@/ts/enums/EHookKeys'
import EPayloadProperties from '@/ts/enums/EPayloadProperties'
import ESessionErrors from '@/ts/enums/ESessionErrors'
import EStorage from '@/ts/enums/EStorage'
import ETokenType from '@/ts/enums/ETokenType'

// utils
import generateMedium from '~/utils/generateMedium'
import getJWTTokenState from '~/utils/getJWTTokenState'

export default defineNuxtPlugin((nuxtApp) => {
    const authLink = setContext(async (_, { headers }) => ({
        headers: {
            ...headers,
            Authorization: `${ETokenType.BEARER} ${useCookie(ECookies.TOKEN).value || ''}`,
            globalUuid: localStorage ? localStorage.getItem(EStorage.MULTI_SHOPPING_CART_UUID) : '',
        },
    }))

    const httpLinkApollo = authLink.concat(
        createHttpLink({
            uri: useRuntimeConfig().public.api.acse,
        }),
    )

    const tokenLink = new TokenRefreshLink({
        isTokenValidOrUndefined: async () => {
            return ![
                EAccessTokenStates.FETCH_TOKEN,
                EAccessTokenStates.NEED_REFRESH,
                EAccessTokenStates.SESSION_EXPIRED,
                EAccessTokenStates.OTHER_APPLICATION_CONTEXT,
            ].includes(
                getJWTTokenState({
                    routePath: nuxtApp.vueApp.$nuxt.$router.currentRoute.value.path,
                }),
            )
        },
        fetchAccessToken: () => {
            const userId = useCookie(ECookies.USER_ID)
            let medium = userId.value || generateMedium()
            const token = useCookie(ECookies.TOKEN)

            const tokenState = getJWTTokenState({
                routePath: nuxtApp.vueApp.$nuxt.$router.currentRoute.value.path,
            })

            let accessToken = ''

            if (tokenState === EAccessTokenStates.NEED_REFRESH) {
                accessToken = token.value || ''
            }

            if (tokenState === EAccessTokenStates.OTHER_APPLICATION_CONTEXT) {
                accessToken = ''
                medium = generateMedium()
            }

            if (
                (tokenState === EAccessTokenStates.NEED_REFRESH ||
                    tokenState === EAccessTokenStates.OTHER_APPLICATION_CONTEXT) &&
                !accessToken
            ) {
                return Promise.reject(new Error(ESessionErrors.SessionExpired))
            }

            let payload: Record<string, string> | string = {}

            payload[EPayloadProperties.GRANT_TYPE] = EGrantType.CLIENT_CREDENTIALS

            if (accessToken) {
                payload[EPayloadProperties.ACCESS_TOKEN] = accessToken
            }

            payload[EPayloadProperties.MEDIUM_WWW] = medium
            payload[EPayloadProperties.SESSION_ID_WWW] = generateSessionId()

            payload = generatePayloadFromObject(payload)

            return fetch(useRuntimeConfig().public.api.token, {
                method: EFetchMethod.POST,
                headers: {
                    [EHeaders.CONTENT_TYPE]: EContentType.APPLICATION_X_WWW_FORM_URLENCODED,
                },
                credentials: EFetchCredentials.OMIT,
                body: payload,
            })
        },
        handleFetch: (accessToken) => {
            const decodedToken = jwtDecode(accessToken) as any

            const todayPlus10Years = new Date(new Date().setFullYear(new Date().getFullYear() + 10))

            const token = useCookie(ECookies.TOKEN, {
                expires: todayPlus10Years,
            })
            const userId = useCookie(ECookies.USER_ID, {
                expires: todayPlus10Years,
            })

            token.value = accessToken
            userId.value = decodedToken.mediumWww
        },
        handleResponse: () => async (response: { json: () => any }) => {
            const data = await response.json()

            return {
                access_token: data.access_token,
            }
        },
        handleError: (error) => {
            console.error('TOKEN: ', error)

            if (error.message === ESessionErrors.SessionExpired) {
                // localStorage.setItem(
                //     EStorage.MARKET_SEGMENT,
                //     useMarketType(nuxtApp.$router.currentRoute.value).marketType.value
                // )

                nuxtApp.vueApp.$nuxt.$router.push('/expired-session').finally(() => {
                    const token = useCookie(ECookies.TOKEN)
                    const userId = useCookie(ECookies.USER_ID)

                    token.value = ''
                    userId.value = ''
                })
            }
        },
    })

    const errorLink = onError((err) => {
        nuxtApp.callHook(EHookKeys.APOLLO_ERROR as any, err)
    })

    const httpLinkPrometheus = createHttpLink({
        uri: useRuntimeConfig().public.api.prometheus,
    })

    const httpLinkSubscription = createHttpLink({
        uri: useRuntimeConfig().public.api.subscription,
    })

    const wsLinkSubscription = new GraphQLWsLink(
        createClient({
            url: useRuntimeConfig().public.api.subscription.replace('https', 'wss'),
        }),
    )

    const subscriptionLink = split(
        ({ query }) => {
            const definition = getMainDefinition(query)
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
        },
        wsLinkSubscription,
        httpLinkSubscription,
    )

    nuxtApp.vueApp.$nuxt.$apollo.defaultClient?.setLink(from([errorLink, tokenLink, httpLinkApollo]))
    nuxtApp.vueApp.$nuxt.$apollo.clients[EApolloClients.PROMETHEUS]?.setLink(from([httpLinkPrometheus]))
    nuxtApp.vueApp.$nuxt.$apollo.clients[EApolloClients.APOLLO_SUBSCRIPTION]?.setLink(from([subscriptionLink]))
})